import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  approveAdminUser,
  getAdminUserDetail,
  getAdminUserList,
  updateAdminUser,
} from '../../lib/api/admin-user-api';
import MembershipModal from './MembershipModal';
import './UserManagement.css';

const INITIAL_FILTERS = {
  userId: '',
  email: '',
  userName: '',
  status: '',
  userType: '',
  isAdmin: '',
  approvedYn: '', // '' 전체 / 'N' 가입요청(미승인) / 'Y' 승인완료
};

const INITIAL_EDIT_FORM = {
  email: '',
  userName: '',
  nickname: '',
  phone: '',
  birthDate: '',
  gender: '',
  userType: 'NORMAL',
  status: 'ACTIVE',
  isAdmin: false,
  isActive: true,
  approvedYn: 'N',
};

function toDateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && value.length >= 10) {
    return value.slice(0, 10);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString();
}

function isApprovedUser(userInfo) {
  return String(userInfo?.approvedYn || 'N').toUpperCase() === 'Y';
}

function UserManagement() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [selectedUser, setSelectedUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [membershipUser, setMembershipUser] = useState(null);

  const [approvingUserId, setApprovingUserId] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const canUseAdminScreen = useMemo(() => !!user?.isAdmin, [user]);

  const loadUserList = async (nextFilters = filters, nextPageNumber = pageNumber, nextPageSize = pageSize) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        pageNumber: nextPageNumber,
        pageSize: nextPageSize,
        ...Object.fromEntries(Object.entries(nextFilters).filter(([, value]) => value !== '')),
      };

      const data = await getAdminUserList(token, params);
      setUsers(data.users || []);
      setTotalCount(data.totalCount || 0);
      setPageNumber(data.pageNumber || nextPageNumber);
      setPageSize(data.pageSize || nextPageSize);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      console.error('사용자 목록 조회 실패:', err);
      setError(err.message || '사용자 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetail = async (targetUserId) => {
    setDetailLoading(true);
    try {
      const data = await getAdminUserDetail(token, targetUserId);
      setSelectedUser(data);
      return data;
    } catch (err) {
      console.error('사용자 상세 조회 실패:', err);
      alert(err.message || '사용자 상세 조회에 실패했습니다.');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadUserList(filters, pageNumber, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPageNumber(1);
    loadUserList(filters, 1, pageSize);
  };

  // 가입요청/전체 탭 전환 — 승인상태(approvedYn)로 서버 필터링
  const handleSelectApprovalTab = (approvedYn) => {
    const nextFilters = { ...filters, approvedYn };
    setFilters(nextFilters);
    setSelectedUser(null);
    setPageNumber(1);
    loadUserList(nextFilters, 1, pageSize);
  };

  const handleReset = () => {
    setFilters(INITIAL_FILTERS);
    setSelectedUser(null);
    setPageNumber(1);
    loadUserList(INITIAL_FILTERS, 1, pageSize);
  };

  const handleCloseDetail = () => {
    setSelectedUser(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'status-active';
      case 'INACTIVE':
        return 'status-inactive';
      case 'SUSPENDED':
        return 'status-suspended';
      default:
        return '';
    }
  };

  const getUserTypeBadgeClass = (userType) => {
    switch (userType) {
      case 'NORMAL':
        return 'type-normal';
      case 'PREMIUM':
        return 'type-premium';
      case 'VIP':
        return 'type-vip';
      default:
        return '';
    }
  };

  const handleApprove = async (targetUserId) => {
    const shouldProceed = window.confirm('해당 사용자를 승인하시겠습니까?');
    if (!shouldProceed) return;

    setApprovingUserId(targetUserId);
    try {
      const result = await approveAdminUser(token, targetUserId);
      alert(result?.message || '승인 처리되었습니다.');
      await loadUserList();
      if (selectedUser?.id === targetUserId) {
        await loadUserDetail(targetUserId);
      }
    } catch (err) {
      console.error('사용자 승인 실패:', err);
      alert(err.message || '승인 처리에 실패했습니다.');
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleOpenEditModal = async (targetUserId) => {
    setEditLoading(true);
    try {
      const detail = selectedUser?.id === targetUserId
        ? selectedUser
        : await getAdminUserDetail(token, targetUserId);

      setEditUserId(targetUserId);
      setEditForm({
        email: detail?.email || '',
        userName: detail?.userName || '',
        nickname: detail?.nickname || '',
        phone: detail?.phone || '',
        birthDate: toDateInputValue(detail?.birthDate),
        gender: detail?.gender || '',
        userType: detail?.userType || 'NORMAL',
        status: detail?.status || 'ACTIVE',
        isAdmin: !!detail?.isAdmin,
        isActive: !!detail?.isActive,
        approvedYn: (detail?.approvedYn || 'N').toUpperCase() === 'Y' ? 'Y' : 'N',
      });
      setShowEditModal(true);
    } catch (err) {
      console.error('수정용 사용자 정보 조회 실패:', err);
      alert(err.message || '수정할 사용자 정보를 가져오지 못했습니다.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditUserId(null);
    setEditForm(INITIAL_EDIT_FORM);
    setEditSaving(false);
    setEditLoading(false);
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editUserId) return;

    setEditSaving(true);
    try {
      await updateAdminUser(token, editUserId, {
        email: editForm.email.trim(),
        userName: editForm.userName.trim(),
        nickname: editForm.nickname.trim(),
        phone: editForm.phone.trim(),
        birthDate: editForm.birthDate || null,
        gender: editForm.gender,
        userType: editForm.userType,
        status: editForm.status,
        isAdmin: editForm.isAdmin,
        isActive: editForm.isActive,
        approvedYn: editForm.approvedYn,
      });

      alert('사용자 정보가 수정되었습니다.');
      handleCloseEditModal();
      await loadUserList();
      await loadUserDetail(editUserId);
    } catch (err) {
      console.error('사용자 정보 수정 실패:', err);
      alert(err.message || '사용자 정보 수정에 실패했습니다.');
    } finally {
      setEditSaving(false);
    }
  };

  if (!canUseAdminScreen) {
    return (
      <div className="user-management">
        <div className="error-message">
          관리자 권한이 필요합니다. (403 Forbidden)
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>회원 관리</h2>

        <div className="user-management-tabs" role="tablist">
          <button
            type="button"
            className={`um-tab ${filters.approvedYn === '' ? 'active' : ''}`}
            onClick={() => handleSelectApprovalTab('')}
          >
            전체 회원
          </button>
          <button
            type="button"
            className={`um-tab um-tab-pending ${filters.approvedYn === 'N' ? 'active' : ''}`}
            onClick={() => handleSelectApprovalTab('N')}
          >
            가입 요청
            {filters.approvedYn === 'N' && totalCount > 0 ? ` (${totalCount.toLocaleString()})` : ''}
          </button>
          <button
            type="button"
            className={`um-tab ${filters.approvedYn === 'Y' ? 'active' : ''}`}
            onClick={() => handleSelectApprovalTab('Y')}
          >
            승인 완료
          </button>
        </div>

        <div className="user-filters">
          <input
            type="text"
            placeholder="아이디"
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
          />
          <input
            type="text"
            placeholder="이메일"
            value={filters.email}
            onChange={(e) => handleFilterChange('email', e.target.value)}
          />
          <input
            type="text"
            placeholder="이름"
            value={filters.userName}
            onChange={(e) => handleFilterChange('userName', e.target.value)}
          />
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">상태 전체</option>
            <option value="ACTIVE">활성</option>
            <option value="INACTIVE">비활성</option>
            <option value="SUSPENDED">정지</option>
          </select>
          <select
            value={filters.userType}
            onChange={(e) => handleFilterChange('userType', e.target.value)}
          >
            <option value="">유형 전체</option>
            <option value="NORMAL">일반</option>
            <option value="PREMIUM">프리미엄</option>
            <option value="VIP">VIP</option>
          </select>
          <select
            value={filters.isAdmin}
            onChange={(e) => handleFilterChange('isAdmin', e.target.value)}
          >
            <option value="">권한 전체</option>
            <option value="true">관리자</option>
            <option value="false">일반 사용자</option>
          </select>

          <div className="filter-actions">
            <button className="btn-search" onClick={handleSearch}>
              검색
            </button>
            <button className="btn-reset" onClick={handleReset}>
              초기화
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-message">로딩 중...</div>
      ) : (
        <>
          <div className="user-table-info">
            <span>전체 {totalCount.toLocaleString()}명</span>
            <span>페이지 {pageNumber} / {totalPages}</span>
          </div>

          <div className="user-table-wrapper">
            <table className="user-table">
              <thead>
                <tr>
                  <th>가입승인</th>
                  <th>ID</th>
                  <th>아이디</th>
                  <th>이름</th>
                  <th>닉네임</th>
                  <th>이메일</th>
                  <th>전화번호</th>
                  <th>상태</th>
                  <th>관리자</th>
                  <th>승인</th>
                  <th>멤버십</th>
                  <th>시작일</th>
                  <th>종료일</th>
                  <th>마지막 로그인</th>
                  <th>가입일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((item) => (
                    <tr key={item.id} className="clickable-row">
                      <td>
                        {isApprovedUser(item) ? (
                          <span className="approval-badge approved">승인완료</span>
                        ) : (
                          <button
                            className="btn-approve"
                            disabled={approvingUserId === item.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(item.id);
                            }}
                          >
                            {approvingUserId === item.id ? '처리 중...' : '가입승인'}
                          </button>
                        )}
                      </td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.id}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.userId}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.userName}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.nickname || '-'}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.email}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.phone || '-'}</td>
                      <td onClick={() => loadUserDetail(item.id)}>
                        <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.isAdmin ? '예' : '-'}</td>
                      <td onClick={() => loadUserDetail(item.id)}>
                        <span className={`approval-badge ${isApprovedUser(item) ? 'approved' : 'pending'}`}>
                          {isApprovedUser(item) ? '승인' : '미승인'}
                        </span>
                      </td>
                      <td onClick={() => loadUserDetail(item.id)}>
                        {item.membership?.membershipType ? (
                          <span className={`membership-badge membership-${item.membership.membershipType.toLowerCase()}`}>
                            {item.membership.membershipType}
                          </span>
                        ) : '-'}
                      </td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.membership?.startDate || '-'}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{item.membership?.endDate || '-'}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{formatDateTime(item.lastLoginAt)}</td>
                      <td onClick={() => loadUserDetail(item.id)}>{formatDate(item.createdAt)}</td>
                      <td>
                        <div className="user-action-buttons">
                          <button
                            className="btn-edit"
                            disabled={editLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(item.id);
                            }}
                          >
                            관리
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="16" className="no-data">
                      검색 결과가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber === 1}
              >
                이전
              </button>
              <span>
                {pageNumber} / {totalPages}
              </span>
              <button
                onClick={() => setPageNumber(Math.min(totalPages, pageNumber + 1))}
                disabled={pageNumber === totalPages}
              >
                다음
              </button>
            </div>
          )}
        </>
      )}

      {selectedUser && (
        <div className="user-detail-modal" onClick={handleCloseDetail}>
          <div className="user-detail-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-detail-header">
              <h3>사용자 상세 정보</h3>
              <button className="close-button" onClick={handleCloseDetail}>
                ×
              </button>
            </div>

            {detailLoading ? (
              <div className="loading-message">로딩 중...</div>
            ) : (
              <div className="user-detail-body">
                <div className="detail-section">
                  <h4>기본 정보</h4>
                  <div className="detail-row">
                    <span className="detail-label">ID:</span>
                    <span className="detail-value">{selectedUser.id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">아이디:</span>
                    <span className="detail-value">{selectedUser.userId}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">이름:</span>
                    <span className="detail-value">{selectedUser.userName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">이메일:</span>
                    <span className="detail-value">{selectedUser.email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">전화번호:</span>
                    <span className="detail-value">{selectedUser.phone || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">생년월일:</span>
                    <span className="detail-value">{toDateInputValue(selectedUser.birthDate) || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">성별:</span>
                    <span className="detail-value">{selectedUser.gender || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">닉네임:</span>
                    <span className="detail-value">{selectedUser.nickname || '-'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>계정 정보</h4>
                  <div className="detail-row">
                    <span className="detail-label">상태:</span>
                    <span className={`status-badge ${getStatusBadgeClass(selectedUser.status)}`}>
                      {selectedUser.status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">회원 유형:</span>
                    <span className={`type-badge ${getUserTypeBadgeClass(selectedUser.userType)}`}>
                      {selectedUser.userType}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">관리자 권한:</span>
                    <span className="detail-value">{selectedUser.isAdmin ? '예' : '아니오'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">승인 상태:</span>
                    <span className="detail-value">{isApprovedUser(selectedUser) ? '승인' : '미승인'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">활성 여부:</span>
                    <span className="detail-value">{selectedUser.isActive ? '활성' : '비활성'}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>보안 정보</h4>
                  <div className="detail-row">
                    <span className="detail-label">로그인 실패 횟수:</span>
                    <span className="detail-value">{selectedUser.loginFailCount}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">계정 잠금:</span>
                    <span className="detail-value">
                      {selectedUser.lockedUntil ? `${formatDateTime(selectedUser.lockedUntil)}까지 잠금` : '잠금 없음'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">비밀번호 변경일:</span>
                    <span className="detail-value">{formatDateTime(selectedUser.passwordChangedAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">이메일 인증:</span>
                    <span className="detail-value">
                      {selectedUser.emailVerified ? '인증됨' : '미인증'}
                      {selectedUser.emailVerifiedAt ? ` (${formatDateTime(selectedUser.emailVerifiedAt)})` : ''}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>활동 정보</h4>
                  <div className="detail-row">
                    <span className="detail-label">마지막 로그인:</span>
                    <span className="detail-value">{formatDateTime(selectedUser.lastLoginAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">가입일:</span>
                    <span className="detail-value">{formatDateTime(selectedUser.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">수정일:</span>
                    <span className="detail-value">{formatDateTime(selectedUser.updatedAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">삭제일:</span>
                    <span className="detail-value">{formatDateTime(selectedUser.deletedAt)}</span>
                  </div>
                </div>

                <div className="detail-actions">
                  {!isApprovedUser(selectedUser) && (
                    <button
                      className="btn-approve"
                      disabled={approvingUserId === selectedUser.id}
                      onClick={() => handleApprove(selectedUser.id)}
                    >
                      {approvingUserId === selectedUser.id ? '처리 중...' : '관리자 승인'}
                    </button>
                  )}
                  <button
                    className="btn-edit"
                    onClick={() => handleOpenEditModal(selectedUser.id)}
                  >
                    사용자 정보 수정
                  </button>
                </div>

                {selectedUser.profileImageUrl && (
                  <div className="detail-section">
                    <h4>프로필 이미지</h4>
                    <img
                      src={selectedUser.profileImageUrl}
                      alt="프로필"
                      className="profile-image"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="user-edit-modal" onClick={handleCloseEditModal}>
          <div className="user-edit-content" onClick={(e) => e.stopPropagation()}>
            <div className="user-edit-header">
              <h3>사용자 정보 수정</h3>
              <button className="close-button" onClick={handleCloseEditModal}>
                ×
              </button>
            </div>
            <form className="user-edit-form" onSubmit={handleSaveEdit}>
              <div className="edit-grid">
                <label>
                  이메일
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => handleEditFieldChange('email', e.target.value)}
                    required
                  />
                </label>
                <label>
                  이름
                  <input
                    type="text"
                    value={editForm.userName}
                    onChange={(e) => handleEditFieldChange('userName', e.target.value)}
                    required
                  />
                </label>
                <label>
                  닉네임
                  <input
                    type="text"
                    value={editForm.nickname}
                    onChange={(e) => handleEditFieldChange('nickname', e.target.value)}
                  />
                </label>
                <label>
                  전화번호
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                  />
                </label>
                <label>
                  생년월일
                  <input
                    type="date"
                    value={editForm.birthDate}
                    onChange={(e) => handleEditFieldChange('birthDate', e.target.value)}
                  />
                </label>
                <label>
                  성별
                  <select
                    value={editForm.gender}
                    onChange={(e) => handleEditFieldChange('gender', e.target.value)}
                  >
                    <option value="">선택 안 함</option>
                    <option value="MALE">남성</option>
                    <option value="FEMALE">여성</option>
                    <option value="OTHER">기타</option>
                  </select>
                </label>
                <label>
                  회원 유형
                  <select
                    value={editForm.userType}
                    onChange={(e) => handleEditFieldChange('userType', e.target.value)}
                  >
                    <option value="NORMAL">NORMAL</option>
                    <option value="PREMIUM">PREMIUM</option>
                    <option value="VIP">VIP</option>
                  </select>
                </label>
                <label>
                  계정 상태
                  <select
                    value={editForm.status}
                    onChange={(e) => handleEditFieldChange('status', e.target.value)}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="SUSPENDED">SUSPENDED</option>
                  </select>
                </label>
                <label>
                  승인 상태
                  <select
                    value={editForm.approvedYn}
                    onChange={(e) => handleEditFieldChange('approvedYn', e.target.value)}
                  >
                    <option value="Y">승인</option>
                    <option value="N">미승인</option>
                  </select>
                </label>
                <label>
                  관리자 권한
                  <select
                    value={editForm.isAdmin ? 'Y' : 'N'}
                    onChange={(e) => handleEditFieldChange('isAdmin', e.target.value === 'Y')}
                  >
                    <option value="N">없음</option>
                    <option value="Y">있음</option>
                  </select>
                </label>
                <label>
                  활성 여부
                  <select
                    value={editForm.isActive ? 'Y' : 'N'}
                    onChange={(e) => handleEditFieldChange('isActive', e.target.value === 'Y')}
                  >
                    <option value="Y">활성</option>
                    <option value="N">비활성</option>
                  </select>
                </label>
              </div>
              <div className="user-edit-actions">
                <button
                  type="button"
                  className="btn-membership"
                  onClick={() => {
                    const target = users.find((u) => u.id === editUserId) || selectedUser;
                    if (target) {
                      setMembershipUser(target);
                      setShowMembershipModal(true);
                    }
                  }}
                >
                  멤버십 관리
                </button>
                <div className="user-edit-actions-right">
                  <button type="button" className="btn-reset" onClick={handleCloseEditModal}>
                    취소
                  </button>
                  <button type="submit" className="btn-search" disabled={editSaving}>
                    {editSaving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMembershipModal && membershipUser && (
        <MembershipModal
          user={membershipUser}
          onClose={() => {
            setShowMembershipModal(false);
            setMembershipUser(null);
          }}
          onUpdate={() => {
            setShowMembershipModal(false);
            setMembershipUser(null);
            loadUserList();
          }}
        />
      )}
    </div>
  );
}

export default UserManagement;
